import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Play } from 'lucide-react';
import { cn } from '../lib/utils';

const formatDuration = (d) => {
  if (!d && d !== 0) return null;
  if (typeof d === 'string') return d;
  if (d < 60) return `${d}s`;
  const m = Math.floor(d / 60);
  const s = d % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const yearOf = (v) =>
  v.release_year ||
  (v.release_date ? new Date(v.release_date).getFullYear() : null);

const cardBase =
  'group relative cursor-pointer select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background';

const posterImageHover =
  'transition-transform duration-300 ease-out group-hover:scale-[1.03] motion-reduce:group-hover:scale-100';

const VideoCard = ({ video, className, style, variant = 'poster' }) => {
  const navigate = useNavigate();

  const byline = video.creator?.name || video.studio?.name;
  const dur = formatDuration(video.duration);
  const hasProg = (video.progress ?? 0) > 0;
  const year = yearOf(video);

  const watchHref = video.slug
    ? `/watch/${video.slug}`
    : `/video/${video.id}`;

  const handleClick = () => navigate(watchHref);

  if (variant === 'continue') {
    return (
      <article
        onClick={handleClick}
        style={style}
        className={cn(cardBase, className)}
      >
        <div className="relative aspect-video overflow-hidden rounded-lg bg-[hsl(var(--kinora-card-hi))] ring-1 ring-transparent transition-[box-shadow,ring-color] duration-300 group-hover:ring-primary/25 group-hover:shadow-md">
          {video.thumbnail ? (
            <img
              src={video.thumbnail}
              alt={video.title}
              draggable={false}
              loading="lazy"
              className={cn('absolute inset-0 h-full w-full object-cover', posterImageHover)}
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-primary/15 via-card to-card" />
          )}
          <div className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-opacity group-hover:bg-black/25 group-hover:opacity-100">
            <Play className="h-9 w-9 fill-white text-white" />
          </div>
          {hasProg && (
            <div className="absolute bottom-0 left-0 right-0 z-10 h-[3px] bg-white/20">
              <div
                className="h-full bg-primary"
                style={{ width: `${video.progress}%` }}
              />
            </div>
          )}
        </div>
        <p className="mt-1.5 line-clamp-1 text-sm font-medium text-foreground group-hover:text-primary">
          {video.title}
        </p>
      </article>
    );
  }

  const isPoster = variant === 'poster';

  return (
    <article
      onClick={handleClick}
      style={style}
      className={cn(cardBase, className)}
    >
      <div
        className={cn(
          'relative overflow-hidden rounded-lg bg-[hsl(var(--kinora-card-hi))]',
          'ring-1 ring-transparent transition-[box-shadow,ring-color] duration-300 group-hover:ring-primary/25 group-hover:shadow-md',
          isPoster ? 'aspect-[2/3]' : 'aspect-video'
        )}
      >
        {video.thumbnail ? (
          <img
            src={video.thumbnail}
            alt={video.title}
            draggable={false}
            loading="lazy"
            className={cn('absolute inset-0 h-full w-full object-cover', posterImageHover)}
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-primary/15 via-card to-card" />
        )}

        {!isPoster && dur && (
          <span className="absolute right-2 top-2 z-10 rounded bg-black/60 px-1.5 py-0.5 text-xs tabular-nums text-white/90">
            {dur}
          </span>
        )}

        <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity group-hover:opacity-100">
          <div className="rounded-full bg-black/50 p-2 backdrop-blur-sm">
            <Play className="h-4 w-4 fill-white text-white" />
          </div>
        </div>

        {hasProg && (
          <div className="absolute bottom-0 left-0 right-0 z-10 h-[3px] bg-white/20">
            <div
              className="h-full bg-primary"
              style={{ width: `${video.progress}%` }}
            />
          </div>
        )}
      </div>

      <div className="mt-2 px-0.5">
        <h3 className="line-clamp-2 text-sm font-medium leading-snug text-foreground group-hover:text-primary">
          {video.title}
        </h3>
        {(byline || year) && (
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            {[byline, year].filter(Boolean).join(' · ')}
          </p>
        )}
      </div>
    </article>
  );
};

export default VideoCard;
